import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Trash2, ChevronLeft, ChevronRight, Users as UsersIcon, GraduationCap, BookOpen, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLE_LABELS = { student: 'Étudiant', instructor: 'Instructeur', admin: 'Administrateur' };
const ROLE_COLORS = {
  admin:      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  instructor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  student:    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

const PAGE_SIZE = 25;

const UsersAdmin = () => {
  const [users, setUsers]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState('');
  const [roleFilter, setRoleFilter]     = useState('all');
  const [changingRole, setChangingRole] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);

  const [roleCounts, setRoleCounts] = useState({ student: 0, instructor: 0, admin: 0, total: 0 });

  const loadUsers = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, page: p };
      if (search) params.search = search;
      if (roleFilter !== 'all') params.role = roleFilter;
      const { data } = await axios.get('/api/admin/users', { params });
      if (data.success) {
        setUsers(data.users || []);
        setTotal(data.total || 0);
      }
    } catch {
      toast.error('Erreur chargement utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, page]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  useEffect(() => { loadUsers(page); }, [loadUsers, page]);

  // Comptage par rôle (appel sans filtre pour les stats)
  useEffect(() => {
    Promise.all([
      axios.get('/api/admin/users', { params: { limit: 1, page: 1 } }),
      axios.get('/api/admin/users', { params: { limit: 1, page: 1, role: 'student' } }),
      axios.get('/api/admin/users', { params: { limit: 1, page: 1, role: 'instructor' } }),
      axios.get('/api/admin/users', { params: { limit: 1, page: 1, role: 'admin' } }),
    ]).then(([all, students, instructors, admins]) => {
      setRoleCounts({
        total:      all.data.total      || 0,
        student:    students.data.total    || 0,
        instructor: instructors.data.total || 0,
        admin:      admins.data.total      || 0,
      });
    }).catch(() => {});
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    setChangingRole(userId);
    try {
      const { data } = await axios.patch(`/api/admin/users/${userId}/role`, { role: newRole });
      if (data.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
        toast.success(`Rôle mis à jour : ${ROLE_LABELS[newRole]}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur changement de rôle');
    } finally {
      setChangingRole(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Supprimer définitivement ${userName} ? Cette action est irréversible.`)) return;
    setDeletingUser(userId);
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setTotal(prev => prev - 1);
      toast.success('Utilisateur supprimé');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    } finally {
      setDeletingUser(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-0 sm:px-2 lg:px-4 py-4 sm:py-6 lg:py-8">

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">Gestion des utilisateurs</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
          {total} utilisateur{total !== 1 ? 's' : ''} au total
        </p>

        {/* Stats par rôle */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { icon: UsersIcon,    label: 'Total',         value: roleCounts.total,      color: 'bg-blue-500',   filter: 'all' },
            { icon: GraduationCap,label: 'Étudiants',     value: roleCounts.student,    color: 'bg-green-500',  filter: 'student' },
            { icon: BookOpen,     label: 'Instructeurs',  value: roleCounts.instructor, color: 'bg-yellow-500', filter: 'instructor' },
            { icon: Shield,       label: 'Admins',        value: roleCounts.admin,      color: 'bg-red-500',    filter: 'admin' },
          ].map(s => (
            <button
              key={s.label}
              onClick={() => setRoleFilter(s.filter)}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex items-center gap-3 text-left transition-all ${
                roleFilter === s.filter ? 'ring-2 ring-primary' : 'hover:shadow-md'
              }`}
            >
              <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center shrink-0`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{s.label}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Filtres */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-5 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Tous les rôles</option>
              <option value="student">Étudiants</option>
              <option value="instructor">Instructeurs</option>
              <option value="admin">Admins</option>
            </select>
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center py-12 text-gray-500">Aucun utilisateur trouvé</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300">Utilisateur</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300">Rôle</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300 hidden md:table-cell">Abonnement</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300 hidden sm:table-cell">Inscription</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300 hidden lg:table-cell">Dernière activité</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900 dark:text-white">{user.first_name} {user.last_name}</div>
                        {user.email && <div className="text-xs text-gray-500 truncate max-w-[200px]">{user.email}</div>}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[user.role] || ROLE_COLORS.student}`}>
                          {ROLE_LABELS[user.role] || user.role}
                        </span>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">
                        <span className={`text-xs ${user.subscription_is_active ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                          {user.subscription_is_active
                            ? (user.subscription_plan || 'Actif')
                            : 'Gratuit'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-500 hidden sm:table-cell">
                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-500 hidden lg:table-cell">
                        {user.last_active ? new Date(user.last_active).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <select
                            value={user.role}
                            onChange={e => handleRoleChange(user.id, e.target.value)}
                            disabled={changingRole === user.id}
                            className="text-xs px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-primary disabled:opacity-50 dark:bg-gray-700 dark:text-white"
                          >
                            <option value="student">Étudiant</option>
                            <option value="instructor">Instructeur</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                            disabled={deletingUser === user.id}
                            className="p-1.5 text-red-500 hover:text-red-700 disabled:opacity-50"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-500">
                Page {page} / {totalPages} — {total} résultats
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersAdmin;
